/*
 * Copyright Mimic Networks, Inc. 2026.
 */

import { CaretRightOutlined, LoadingOutlined, SendOutlined } from '@ant-design/icons';
import axios from 'axios';
import { useCallback, useEffect, useMemo, useState } from 'react';
import Editor from 'react-simple-code-editor';

import { JsonNode } from '@/components/JsonTreeViewer/JsonTreeViewer';
import { CopyButton } from '@/primitives/CopyButton';

import { generateExample } from './schemaUtils';
import { Endpoint } from './types';

type BodyMode = 'fields' | 'json';

const BODY_MODE_KEY = 'apiref-tryit-mode';

function getStoredBodyMode(): BodyMode {
  try {
    const stored = localStorage.getItem(BODY_MODE_KEY);
    if (stored === 'fields') return 'fields';
  } catch {
    // localStorage unavailable
  }
  return 'json';
}

function storeBodyMode(mode: BodyMode) {
  try {
    localStorage.setItem(BODY_MODE_KEY, mode);
  } catch {
    // localStorage unavailable
  }
}

interface TryItPanelProps {
  endpoint: Endpoint;
  pathParams: NonNullable<Endpoint['parameters']>;
  queryParams: NonNullable<Endpoint['parameters']>;
  bodySchema: Record<string, unknown> | null;
  rawBodySchema: Record<string, unknown> | null;
  schemas: Record<string, unknown>;
  authToken: string | null;
  tenantID?: string;
  subtenantID?: string;
  resolveSchema: (schema: Record<string, unknown>) => Record<string, unknown>;
  onCurlChange?: (curl: string) => void;
  selectedVariantSchema?: Record<string, unknown> | null;
}

interface RequestResult {
  status: number;
  statusText: string;
  data: unknown;
  duration: number;
}

interface BodyField {
  name: string;
  path: string;
  type: string;
  typeLabel: string;
  required: boolean;
  depth: number;
}

interface FilterField {
  name: string;
  type: string;
}

interface FilterParam {
  paramName: string;
  fields: FilterField[];
}

interface SortParam {
  paramName: string;
  fieldNames: string[];
}

type QueryParam = NonNullable<Endpoint['parameters']>[number];

function getSmartDefault(paramName: string, tenantID?: string, subtenantID?: string): string {
  const lower = paramName.toLowerCase();

  if (lower === 'tenantid' && tenantID) return tenantID;
  if (lower === 'subtenantid' && subtenantID) return subtenantID;
  if (lower === 'page') return '1';
  if (lower === 'pagesize') return '20';

  return '';
}

function formatTypeLabel(prop: Record<string, unknown>): string {
  if (prop.enum) return (prop.enum as string[]).join(' | ');

  const base = (prop.type as string) || 'object';

  if (base === 'array') {
    const items = prop.items as Record<string, unknown> | undefined;
    if (items?.enum) return `(${(items.enum as string[]).join(' | ')})[]`;
    if (items?.type) return `${items.type as string}[]`;
    return 'array';
  }

  if (prop.format) return `${base} (${prop.format as string})`;

  return base;
}

function extractBodyFields(schema: Record<string, unknown> | null, prefix = '', depth = 0, maxDepth = 3): BodyField[] {
  if (!schema || !schema.properties || depth > maxDepth) return [];

  const props = schema.properties as Record<string, Record<string, unknown>>;
  const requiredList = (schema.required as string[]) || [];
  const fields: BodyField[] = [];

  Object.entries(props).forEach(([name, prop]) => {
    const path = prefix ? `${prefix}.${name}` : name;
    const type = (prop.type as string) || 'string';

    fields.push({
      name,
      path,
      type,
      typeLabel: formatTypeLabel(prop),
      required: requiredList.includes(name),
      depth,
    });

    // Recursively add children for object properties with sub-properties
    if (type === 'object' && prop.properties) {
      fields.push(...extractBodyFields(prop as Record<string, unknown>, path, depth + 1, maxDepth));
    }
  });

  return fields;
}

const UNSAFE_KEYS = new Set(['__proto__', 'constructor', 'prototype']);

function setNestedValue(obj: Record<string, unknown>, path: string, value: unknown) {
  const keys = path.split('.');
  if (keys.length === 0) return;

  let current: Record<string, unknown> = obj;

  for (let i = 0; i < keys.length - 1; i += 1) {
    const k = keys[i];
    if (UNSAFE_KEYS.has(k)) return;

    const next = current[k];
    if (next === undefined || next === null || Object.prototype.toString.call(next) !== '[object Object]') {
      const created: Record<string, unknown> = Object.create(null);
      current[k] = created;
      current = created;
    } else {
      current = next as Record<string, unknown>;
    }
  }

  const lastKey = keys[keys.length - 1];
  if (UNSAFE_KEYS.has(lastKey)) return;
  current[lastKey] = value;
}

function buildBodyFromFields(fields: BodyField[], values: Record<string, string>): Record<string, unknown> {
  const obj: Record<string, unknown> = {};

  fields.forEach((field) => {
    const val = values[field.path];
    if (val === undefined || val === '') return;

    // Skip parent object fields, their children populate the nested structure
    if (field.type === 'object') return;

    let parsed: unknown;

    if (field.type === 'integer' || field.type === 'number') {
      const num = Number(val);
      if (!Number.isNaN(num)) parsed = num;
      else return;
    } else if (field.type === 'boolean') {
      parsed = val === 'true';
    } else if (field.type === 'array') {
      try {
        parsed = JSON.parse(val);
      } catch {
        parsed = val;
      }
    } else {
      parsed = val;
    }

    setNestedValue(obj, field.path, parsed);
  });

  return obj;
}

function categorizeQueryParams(
  params: NonNullable<Endpoint['parameters']>,
  resolve: (schema: Record<string, unknown>) => Record<string, unknown>,
): { regular: QueryParam[]; filters: FilterParam[]; sorts: SortParam[] } {
  const regular: QueryParam[] = [];
  const filters: FilterParam[] = [];
  const sorts: SortParam[] = [];

  params.forEach((p) => {
    if (!p.schema) {
      regular.push(p);
      return;
    }

    const resolved = resolve(p.schema);

    if (resolved.type === 'object' && resolved.properties) {
      const props = resolved.properties as Record<string, Record<string, unknown>>;
      const fields = Object.entries(props).map(([name, prop]) => ({
        name,
        type: (prop.type as string) || 'string',
      }));
      filters.push({ paramName: p.name, fields });
    } else if (resolved.type === 'array' && (resolved.items as Record<string, unknown> | undefined)?.enum) {
      const items = resolved.items as Record<string, unknown>;
      const enumValues = items.enum as string[];
      const fieldNames = [...new Set(enumValues.map((v) => v.replace(/^[+-]/, '')))];
      sorts.push({ paramName: p.name, fieldNames });
    } else {
      regular.push(p);
    }
  });

  return { regular, filters, sorts };
}

function buildCurl(
  method: string,
  url: string,
  body: Record<string, unknown> | null,
  authToken: string | null,
): string {
  const parts = [`curl -X ${method.toUpperCase()}`];

  parts.push(`  '${url}'`);
  parts.push(`  -H 'Content-Type: application/json'`);

  if (authToken) {
    const cookieName = url.startsWith('https://') ? '__Secure-signInSessionId' : 'signInSessionId';
    parts.push(`  -H 'Cookie: ${cookieName}=${authToken}'`);
  }

  if (body && Object.keys(body).length > 0 && ['post', 'put', 'patch'].includes(method)) {
    const json = JSON.stringify(body).replace(/'/g, "'\\''");
    parts.push(`  -d '${json}'`);
  }

  return parts.join(' \\\n');
}

export function TryItPanel({
  endpoint,
  pathParams,
  queryParams,
  bodySchema,
  rawBodySchema,
  schemas,
  authToken,
  tenantID,
  subtenantID,
  resolveSchema,
  onCurlChange,
  selectedVariantSchema,
}: TryItPanelProps) {
  const { regular, filters, sorts } = useMemo(
    () => categorizeQueryParams(queryParams, resolveSchema),
    [queryParams, resolveSchema],
  );

  const allSimpleParams = useMemo(() => [...pathParams, ...regular], [pathParams, regular]);
  const bodyFields = useMemo(() => extractBodyFields(bodySchema), [bodySchema]);

  const [paramValues, setParamValues] = useState<Record<string, string>>(() => {
    const defaults: Record<string, string> = {};

    allSimpleParams.forEach((p) => {
      const val = getSmartDefault(p.name, tenantID, subtenantID);
      if (val) defaults[p.name] = val;
    });

    return defaults;
  });

  const [filterValues, setFilterValues] = useState<Record<string, Record<string, string>>>(() => {
    const defaults: Record<string, Record<string, string>> = {};

    filters.forEach((fp) => {
      const fieldDefaults: Record<string, string> = {};

      fp.fields.forEach((f) => {
        const val = getSmartDefault(f.name, tenantID, subtenantID);
        if (val) fieldDefaults[f.name] = val;
      });

      if (Object.keys(fieldDefaults).length > 0) defaults[fp.paramName] = fieldDefaults;
    });

    return defaults;
  });

  const [sortValues, setSortValues] = useState<Record<string, string[]>>({});
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [sortsOpen, setSortsOpen] = useState(false);

  const [bodyValues, setBodyValues] = useState<Record<string, string>>(() => {
    const defaults: Record<string, string> = {};

    bodyFields.forEach((f) => {
      const val = getSmartDefault(f.name, tenantID, subtenantID);
      if (val) defaults[f.path] = val;
    });

    return defaults;
  });

  const [bodyMode, setBodyMode] = useState<BodyMode>(getStoredBodyMode);

  const exampleSchema = selectedVariantSchema || rawBodySchema;

  const defaultJson = useMemo(() => {
    if (!exampleSchema) return '{}';
    const example = generateExample(exampleSchema, schemas);
    return JSON.stringify(example, null, 2);
  }, [exampleSchema, schemas]);

  const [jsonText, setJsonText] = useState(defaultJson);

  const [result, setResult] = useState<RequestResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const paramDefaults: Record<string, string> = {};

    [...pathParams, ...regular].forEach((p) => {
      const val = getSmartDefault(p.name, tenantID, subtenantID);
      if (val) paramDefaults[p.name] = val;
    });

    setParamValues(paramDefaults);

    const filterDefaults: Record<string, Record<string, string>> = {};

    filters.forEach((fp) => {
      const fieldDefaults: Record<string, string> = {};

      fp.fields.forEach((f) => {
        const val = getSmartDefault(f.name, tenantID, subtenantID);
        if (val) fieldDefaults[f.name] = val;
      });

      if (Object.keys(fieldDefaults).length > 0) filterDefaults[fp.paramName] = fieldDefaults;
    });

    setFilterValues(filterDefaults);
    setSortValues({});

    const fieldDefaults: Record<string, string> = {};
    const fields = extractBodyFields(bodySchema);

    fields.forEach((f) => {
      const val = getSmartDefault(f.name, tenantID, subtenantID);
      if (val) fieldDefaults[f.path] = val;
    });

    setBodyValues(fieldDefaults);
    setJsonText(defaultJson);
    setResult(null);
    setError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [endpoint.operationId, endpoint.method, endpoint.path, tenantID, subtenantID]);

  // Update JSON text when variant changes
  useEffect(() => {
    if (selectedVariantSchema) {
      const example = generateExample(selectedVariantSchema, schemas);
      setJsonText(JSON.stringify(example, null, 2));
    }
  }, [selectedVariantSchema, schemas]);

  const updateParam = useCallback((name: string, value: string) => {
    setParamValues((prev) => ({ ...prev, [name]: value }));
  }, []);

  const updateFilterField = useCallback((paramName: string, fieldName: string, value: string) => {
    setFilterValues((prev) => ({
      ...prev,
      [paramName]: { ...(prev[paramName] || {}), [fieldName]: value },
    }));
  }, []);

  const toggleSort = useCallback((paramName: string, fieldName: string) => {
    setSortValues((prev) => {
      const current = prev[paramName] || [];
      const ascVal = `+${fieldName}`;
      const descVal = `-${fieldName}`;

      if (current.includes(ascVal)) {
        return { ...prev, [paramName]: current.map((v) => (v === ascVal ? descVal : v)) };
      }

      if (current.includes(descVal)) {
        return { ...prev, [paramName]: current.filter((v) => v !== descVal) };
      }

      return { ...prev, [paramName]: [...current, ascVal] };
    });
  }, []);

  const updateBodyField = useCallback((path: string, value: string) => {
    setBodyValues((prev) => ({ ...prev, [path]: value }));
  }, []);

  const switchBodyMode = useCallback((mode: BodyMode) => {
    setBodyMode(mode);
    storeBodyMode(mode);
  }, []);

  const currentUrl = useMemo(() => {
    let url = `${window.mimicConfig.MIMIC_MGMT_PLANE_API}${endpoint.path}`;

    pathParams.forEach((p) => {
      const val = paramValues[p.name] || `{${p.name}}`;
      url = url.replace(`{${p.name}}`, encodeURIComponent(val));
    });

    const qParts: string[] = [];

    regular.forEach((p) => {
      const val = paramValues[p.name];
      if (val) qParts.push(`${encodeURIComponent(p.name)}=${encodeURIComponent(val)}`);
    });

    filters.forEach((fp) => {
      const values = filterValues[fp.paramName] || {};

      Object.entries(values).forEach(([field, val]) => {
        if (val) {
          qParts.push(
            `${encodeURIComponent(fp.paramName)}%5B${encodeURIComponent(field)}%5D=${encodeURIComponent(val)}`,
          );
        }
      });
    });

    sorts.forEach((sp) => {
      const selected = sortValues[sp.paramName] || [];

      if (selected.length > 0) {
        qParts.push(`${sp.paramName}=${selected.map((v) => encodeURIComponent(v)).join(',')}`);
      }
    });

    if (qParts.length) url += `?${qParts.join('&')}`;

    return url;
  }, [endpoint.path, pathParams, paramValues, regular, filters, filterValues, sorts, sortValues]);

  const curlUrl = useMemo(() => {
    const api = String(window.mimicConfig.MIMIC_MGMT_PLANE_API || '');

    if (api.startsWith('https://')) return currentUrl;

    const env = String(window.mimicConfig.MIMIC_MGMT_PLANE_ENVIRONMENT || 'dev');
    const externalBase = env === 'prod' ? 'https://api.mimic.com' : `https://api.${env}.mimic.com`;

    if (api) return currentUrl.replace(api, externalBase);

    return `${externalBase}${currentUrl}`;
  }, [currentUrl]);

  const hasBody = ['post', 'put', 'patch'].includes(endpoint.method);

  // Auto-switch to JSON mode when there are no form fields but a body schema exists
  useEffect(() => {
    if (hasBody && bodyFields.length === 0 && bodySchema && bodyMode === 'fields') {
      setBodyMode('json');
    }
  }, [hasBody, bodyFields.length, bodySchema, bodyMode]);

  const { bodyObj, jsonParseError } = useMemo(() => {
    if (!hasBody) return { bodyObj: null, jsonParseError: null };

    if (bodyMode === 'json') {
      try {
        return { bodyObj: JSON.parse(jsonText) as Record<string, unknown>, jsonParseError: null };
      } catch (e) {
        return { bodyObj: null, jsonParseError: `Invalid JSON: ${(e as SyntaxError).message}` };
      }
    }

    return { bodyObj: buildBodyFromFields(bodyFields, bodyValues), jsonParseError: null };
  }, [hasBody, bodyMode, jsonText, bodyFields, bodyValues]);

  useEffect(() => {
    if (onCurlChange) {
      onCurlChange(buildCurl(endpoint.method, curlUrl, bodyObj, authToken));
    }
  }, [endpoint.method, curlUrl, bodyObj, authToken, onCurlChange]);

  const execute = async () => {
    if (jsonParseError) {
      setError(jsonParseError);
      return;
    }
    setLoading(true);
    setResult(null);
    setError(null);

    try {
      const config: Record<string, unknown> = {
        method: endpoint.method,
        url: currentUrl,
        withCredentials: true,
        headers: { 'Content-Type': 'application/json' },
        validateStatus: () => true,
      };

      if (hasBody && bodyObj && Object.keys(bodyObj).length > 0) {
        config.data = bodyObj;
      }

      const start = performance.now();
      const response = await axios(config as unknown as Parameters<typeof axios>[0]);
      const duration = Math.round(performance.now() - start);

      setResult({
        status: response.status,
        statusText: response.statusText,
        data: response.data,
        duration,
      });
    } catch (err) {
      setError(String(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="apiref-tryit">
      <div className="apiref-tryit-header">
        <span className="apiref-tryit-title">Try it</span>
        <button type="button" className="apiref-tryit-send" onClick={execute} disabled={loading}>
          {loading ? <LoadingOutlined /> : <SendOutlined />}
          {loading ? 'Sending...' : 'Send'}
        </button>
      </div>

      {allSimpleParams.length > 0 && (
        <div className="apiref-tryit-params">
          {allSimpleParams.map((p) => (
            <div key={p.name} className="apiref-tryit-field">
              <label className="apiref-tryit-label" htmlFor={`tryit-${p.name}`}>
                {p.name}
                {p.required && <span className="apiref-tryit-req">*</span>}
                <span className="apiref-tryit-in">{p.in}</span>
              </label>
              <input
                id={`tryit-${p.name}`}
                className="apiref-tryit-input"
                type="text"
                placeholder={`Enter ${p.name}`}
                value={paramValues[p.name] || ''}
                onChange={(e) => updateParam(p.name, e.target.value)}
              />
            </div>
          ))}
        </div>
      )}

      {filters.some((fp) => fp.fields.length > 0) && (
        <div className="apiref-tryit-params" style={{ borderTop: '1px solid #f0f0f0' }}>
          <button type="button" className="apiref-tryit-section-toggle" onClick={() => setFiltersOpen(!filtersOpen)}>
            <CaretRightOutlined className={`apiref-tryit-section-chevron ${filtersOpen ? 'open' : ''}`} />
            <span className="apiref-tryit-group-label">Filters</span>
          </button>
          {filtersOpen &&
            filters.map((fp) =>
              fp.fields.map((f) =>
                f.type === 'boolean' ? (
                  <div key={`${fp.paramName}-${f.name}`} className="apiref-tryit-field">
                    <span className="apiref-tryit-label">{f.name}</span>
                    <div className="apiref-tryit-radios">
                      <label className="apiref-tryit-radio">
                        <input
                          type="radio"
                          name={`tryit-filter-${fp.paramName}-${f.name}`}
                          checked={filterValues[fp.paramName]?.[f.name] === 'true'}
                          onChange={() => updateFilterField(fp.paramName, f.name, 'true')}
                        />
                        true
                      </label>
                      <label className="apiref-tryit-radio">
                        <input
                          type="radio"
                          name={`tryit-filter-${fp.paramName}-${f.name}`}
                          checked={filterValues[fp.paramName]?.[f.name] === 'false'}
                          onChange={() => updateFilterField(fp.paramName, f.name, 'false')}
                        />
                        false
                      </label>
                    </div>
                  </div>
                ) : (
                  <div key={`${fp.paramName}-${f.name}`} className="apiref-tryit-field">
                    <label className="apiref-tryit-label" htmlFor={`tryit-filter-${fp.paramName}-${f.name}`}>
                      {f.name}
                    </label>
                    <input
                      id={`tryit-filter-${fp.paramName}-${f.name}`}
                      className="apiref-tryit-input"
                      type="text"
                      placeholder={`Filter by ${f.name}`}
                      value={filterValues[fp.paramName]?.[f.name] || ''}
                      onChange={(e) => updateFilterField(fp.paramName, f.name, e.target.value)}
                    />
                  </div>
                ),
              ),
            )}
        </div>
      )}

      {sorts.some((sp) => sp.fieldNames.length > 0) && (
        <div className="apiref-tryit-params" style={{ borderTop: '1px solid #f0f0f0' }}>
          <button type="button" className="apiref-tryit-section-toggle" onClick={() => setSortsOpen(!sortsOpen)}>
            <CaretRightOutlined className={`apiref-tryit-section-chevron ${sortsOpen ? 'open' : ''}`} />
            <span className="apiref-tryit-group-label">Sort</span>
          </button>
          {sortsOpen &&
            sorts.map((sp) => (
              <div key={sp.paramName} className="apiref-tryit-field">
                <div className="apiref-tryit-sorts">
                  {sp.fieldNames.map((fieldName) => {
                    const current = sortValues[sp.paramName] || [];
                    const isAsc = current.includes(`+${fieldName}`);
                    const isDesc = current.includes(`-${fieldName}`);
                    let chipClass = 'apiref-tryit-sort-chip';

                    if (isAsc) chipClass += ' asc';
                    if (isDesc) chipClass += ' desc';

                    return (
                      <button
                        key={fieldName}
                        type="button"
                        className={chipClass}
                        onClick={() => toggleSort(sp.paramName, fieldName)}
                      >
                        {isAsc && '\u2191 '}
                        {isDesc && '\u2193 '}
                        {fieldName}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
        </div>
      )}

      {hasBody && (bodyFields.length > 0 || bodySchema) && (
        <div className="apiref-tryit-params" style={{ borderTop: '1px solid #f0f0f0' }}>
          <div className="apiref-tryit-mode-toggle">
            <button
              type="button"
              className={`apiref-tryit-mode-btn ${bodyMode === 'json' ? 'active' : ''}`}
              onClick={() => switchBodyMode('json')}
            >
              JSON
            </button>
            <button
              type="button"
              className={`apiref-tryit-mode-btn ${bodyMode === 'fields' ? 'active' : ''}`}
              onClick={() => switchBodyMode('fields')}
            >
              Fields
            </button>
          </div>

          {bodyMode === 'fields' &&
            bodyFields.map((f) => {
              const indent = f.depth * 16;

              if (f.type === 'object') {
                return (
                  <div
                    key={f.path}
                    className="apiref-tryit-field apiref-tryit-group-header"
                    style={{ paddingLeft: indent }}
                  >
                    <span className="apiref-tryit-label">
                      {f.name}
                      {f.required && <span className="apiref-tryit-req">*</span>}
                      <span className="apiref-tryit-in">{f.typeLabel}</span>
                    </span>
                  </div>
                );
              }

              if (f.type === 'boolean') {
                return (
                  <div key={f.path} className="apiref-tryit-field" style={{ paddingLeft: indent }}>
                    <span className="apiref-tryit-label">
                      {f.name}
                      {f.required && <span className="apiref-tryit-req">*</span>}
                      <span className="apiref-tryit-in">{f.typeLabel}</span>
                    </span>
                    <div className="apiref-tryit-radios">
                      <label className="apiref-tryit-radio">
                        <input
                          type="radio"
                          name={`tryit-body-${f.path}`}
                          checked={bodyValues[f.path] === 'true'}
                          onChange={() => updateBodyField(f.path, 'true')}
                        />
                        true
                      </label>
                      <label className="apiref-tryit-radio">
                        <input
                          type="radio"
                          name={`tryit-body-${f.path}`}
                          checked={bodyValues[f.path] === 'false'}
                          onChange={() => updateBodyField(f.path, 'false')}
                        />
                        false
                      </label>
                    </div>
                  </div>
                );
              }

              return (
                <div key={f.path} className="apiref-tryit-field" style={{ paddingLeft: indent }}>
                  <label className="apiref-tryit-label" htmlFor={`tryit-body-${f.path}`}>
                    {f.name}
                    {f.required && <span className="apiref-tryit-req">*</span>}
                    <span className="apiref-tryit-in">{f.typeLabel}</span>
                  </label>
                  <input
                    id={`tryit-body-${f.path}`}
                    className="apiref-tryit-input"
                    type="text"
                    placeholder={`Enter ${f.name}`}
                    value={bodyValues[f.path] || ''}
                    onChange={(e) => updateBodyField(f.path, e.target.value)}
                  />
                </div>
              );
            })}

          {bodyMode === 'json' && (
            <>
              <label htmlFor="apiref-json-editor" className="apiref-sr-only">
                Request body JSON
              </label>
              <Editor
                className="apiref-tryit-json-editor"
                textareaId="apiref-json-editor"
                value={jsonText}
                onValueChange={setJsonText}
                highlight={highlightJson}
                padding={16}
                style={{
                  fontFamily: "'SF Mono', 'Fira Code', 'Consolas', monospace",
                  fontSize: 12,
                  lineHeight: 1.6,
                  background: '#1e1e1e',
                  borderRadius: 8,
                  color: '#d4d4d4',
                  minHeight: 180,
                  maxHeight: 400,
                  overflow: 'auto',
                }}
              />
            </>
          )}
        </div>
      )}

      {error && <div className="apiref-tryit-error">{error}</div>}

      {result && (
        <div className="apiref-tryit-result">
          <div className="apiref-tryit-result-header">
            <span className={`apiref-status s${String(result.status)[0]}`}>
              {result.status} {result.statusText}
            </span>
            <span className="apiref-tryit-duration">{result.duration}ms</span>
            <CopyButton textToCopy={JSON.stringify(result.data, null, 2)} size="sm" />
          </div>
          <div className="apiref-tryit-response">
            <JsonNode value={result.data} isLast />
          </div>
        </div>
      )}
    </div>
  );
}

function highlightJson(code: string): string {
  const safe = code.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

  return safe.replace(
    /"(?:[^"\\]|\\.)*"(\s*:)?|(-?\d+(?:\.\d+)?(?:[eE][+-]?\d+)?)(?!\w)|(\btrue\b|\bfalse\b|\bnull\b)/g,
    (m, colon, num, bool) => {
      if (num) return `<span class="json-number">${num}</span>`;
      if (bool) return `<span class="json-bool">${bool}</span>`;
      if (colon) return `<span class="json-key">${m.slice(0, -colon.length)}</span>${colon}`;
      return `<span class="json-string">${m}</span>`;
    },
  );
}
